import * as React from 'react';
import { useState, useEffect } from 'react';
import { FaExternalLinkAlt, FaFileAlt, FaGlobe } from 'react-icons/fa';
import StreamingText from './StreamingText';
import type { RetrievalResult } from '../types';

interface RetrievalResultsProps {
  results: RetrievalResult[];
  maxResults?: number;
  shouldStream?: boolean;
  onStreamComplete?: () => void;
  onStreamUpdate?: () => void;
}

/**
 * Utility function to detect if content should be rendered as CommonMark
 */
const isCommonMarkContent = (content: string, type?: string): boolean => {
  // Check if type explicitly indicates component/markdown
  if (type === 'component') return true;
  
  // Check for markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s+.+$/m,           // Headers
    /\*\*.+\*\*/,               // Bold
    /\*.+\*/,                   // Italic
    /\[.+\]\(.+\)/,             // Links
    /^[-*+]\s+.+$/m,            // Unordered lists
    /^\d+\.\s+.+$/m,            // Ordered lists
    /^>.+$/m,                   // Blockquotes
    /`[^`]+`/,                  // Inline code
    /^```[\s\S]*?```$/m,        // Code blocks
    /^\|.+\|$/m,                // Tables
  ];
  
  return markdownPatterns.some(pattern => pattern.test(content));
};

interface RetrievalResultsProps {
  results: RetrievalResult[];
  maxResults?: number;
  shouldStream?: boolean;
  onStreamComplete?: () => void;
  onStreamUpdate?: () => void;
}

const RetrievalResults: React.FC<RetrievalResultsProps> = ({ 
  results, 
  maxResults = 3,
  shouldStream = false,
  onStreamComplete,
  onStreamUpdate
}) => {
  const [currentStreamingIndex, setCurrentStreamingIndex] = useState(0);
  const [completedStreaming, setCompletedStreaming] = useState<number[]>([]);
  
  const displayResults = results.slice(0, maxResults);

  const handleResultStreamComplete = (index: number) => {
    setCompletedStreaming(prev => [...prev, index]);
    
    if (index < displayResults.length - 1) {
      // Start streaming next result
      setCurrentStreamingIndex(index + 1);
    } else {
      // All results completed
      onStreamComplete?.();
    }
  };

  useEffect(() => {
    if (shouldStream) {
      setCurrentStreamingIndex(0);
      setCompletedStreaming([]);
    }
  }, [shouldStream]);

  if (!displayResults.length) {
    return null;
  }

  const getFileIcon = (uri: string) => {
  if (uri.includes('.html')) return <FaGlobe size={16} />;
  if (uri.includes('.pdf')) return <FaFileAlt size={16} />;
  return <FaFileAlt size={16} />;
  };

  const getFileName = (uri: string) => {
    const parts = uri.split('/');
    return parts[parts.length - 1] || uri;
  };

  return (
    <div className="retrieval-results">
      <div className="retrieval-results__header">
        <h4 className="retrieval-results__title">Sources & References</h4>
        <span className="retrieval-results__count">
          Top {displayResults.length} of {results.length} matches
        </span>
      </div>
      
      <div className="retrieval-results__list">
        {displayResults.map((result, index) => {
          // Only show cards that should be visible:
          // - All cards if not streaming
          // - Current streaming card and all completed cards if streaming
          const shouldShowCard = !shouldStream || 
                                index <= currentStreamingIndex || 
                                completedStreaming.includes(index);
          
          if (!shouldShowCard) {
            return null;
          }
          
          return (
            <div key={index} className="retrieval-result">
              <div className="retrieval-result__content">
                <div className="retrieval-result__text">
                  {(() => {
                    const content = displayResults.length === 1 
                      ? result.content.text // Show complete text for single result
                      : (result.content.text.length > 300 
                        ? result.content.text.substring(0, 300) + '...'
                        : result.content.text);
                    
                    const shouldRenderAsMarkdown = isCommonMarkContent(result.content.text, result.content.type);
                    
                    if (shouldStream) {
                      return (
                        <StreamingText 
                          text={content}
                          speed={120}
                          onComplete={() => handleResultStreamComplete(index)}
                          onTextUpdate={onStreamUpdate}
                          startStreaming={index === currentStreamingIndex || completedStreaming.includes(index)}
                          renderAs={shouldRenderAsMarkdown ? 'markdown' : 'text'}
                        />
                      );
                    } else {
                      return shouldRenderAsMarkdown ? (
                        <></>
                        // <CommonMarkRenderer content={content} />
                      ) : (
                        <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>
                      );
                    }
                  })()}
                </div>
              </div>
              
              <div className="retrieval-result__footer">
                <div className="retrieval-result__source">
                  {getFileIcon(result.location.s3Location?.uri || '')}
                  <span className="retrieval-result__filename">
                    {getFileName(result.location.s3Location?.uri || 'Unknown source')}
                  </span>
                </div>
                
                <a 
                  href={`#source-${index}`}
                  className="retrieval-result__link"
                  title="View source document"
                  onClick={(e) => {
                    e.preventDefault();
                    // Here you could implement opening the source document
                    // You could show a modal, open in new tab, or expand inline
                  }}
                >
                  <FaExternalLinkAlt size={14} />
                  View Source
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RetrievalResults;
